import {
  Car,
  Coffee,
  DoorOpen,
  Dumbbell,
  Lock,
  MapPin,
  Phone,
  Mail,
  Globe,
  ShowerHead,
  ShoppingBag,
  Sun,
  Wifi,
  Accessibility,
} from 'lucide-react';
import type { ClubOperatingHourSlot, Location, OrgAmenityKey } from '@court-sync/shared';
import { ORG_AMENITY_LABELS } from '@court-sync/shared';
import PublicAcademyMap from '@/components/PublicAcademyMap';
import { SportIconTile } from '@/components/sport/SportIcons';
import { getSportLabel } from '@/lib/sportPresentation';
import type { PublicOrgOrganization, PublicOrgSport } from '@/services/publicOrg';

const AMENITY_ICONS: Record<OrgAmenityKey, React.ComponentType<{ className?: string }>> = {
  parking: Car,
  cafe: Coffee,
  changing_rooms: DoorOpen,
  lockers: Lock,
  wifi: Wifi,
  equipment_rental: Dumbbell,
  pro_shop: ShoppingBag,
  accessible_access: Accessibility,
  showers: ShowerHead,
  floodlights: Sun,
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface OrgSidebarProps {
  organization: PublicOrgOrganization;
  amenities: OrgAmenityKey[];
  sports: PublicOrgSport[];
  locations: Location[];
  operatingHours: ClubOperatingHourSlot[] | null;
}

export function OrgSidebar({
  organization,
  amenities,
  sports,
  locations,
  operatingHours,
}: OrgSidebarProps) {
  const mapAddress =
    locations[0]?.address ||
    locations[0]?.googleMapsAddress ||
    organization.googleMapsAddress ||
    organization.address;

  return (
    <aside className="space-y-6" aria-label="Venue information">
      {mapAddress ? (
        <div className="overflow-hidden rounded-xl border border-border">
          <PublicAcademyMap address={mapAddress} name={organization.name} />
        </div>
      ) : null}

      {amenities.length > 0 ? (
        <div>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Amenities
          </h3>
          <ul className="space-y-2">
            {amenities.map((key) => {
              const Icon = AMENITY_ICONS[key];
              return (
                <li key={key} className="flex items-center gap-2 text-sm text-foreground">
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  {ORG_AMENITY_LABELS[key]}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {operatingHours && operatingHours.length > 0 ? (
        <div>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Opening hours
          </h3>
          <ul className="space-y-1 text-sm">
            {operatingHours.map((row) => (
              <li key={row.dayOfWeek} className="flex justify-between gap-2">
                <span className="text-muted-foreground">{DAY_NAMES[row.dayOfWeek]}</span>
                <span>
                  {row.startTime.slice(0, 5)} – {row.endTime.slice(0, 5)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {sports.length > 0 ? (
        <div>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Sports
          </h3>
          <ul className="flex flex-wrap gap-2">
            {sports.map((sport) => (
              <li
                key={sport.sportId}
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs"
              >
                <SportIconTile sportId={sport.sportId} className="h-4 w-4" />
                {getSportLabel(sport)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Contact
        </h3>
        <ul className="space-y-2 text-sm">
          {organization.address ? (
            <li className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span>{organization.address}</span>
            </li>
          ) : null}
          {organization.phone ? (
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <a href={`tel:${organization.phone}`} className="hover:underline">
                {organization.phone}
              </a>
            </li>
          ) : null}
          {organization.email ? (
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <a href={`mailto:${organization.email}`} className="hover:underline">
                {organization.email}
              </a>
            </li>
          ) : null}
          {organization.website ? (
            <li className="flex items-center gap-2">
              <Globe className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <a
                href={organization.website}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                Website
              </a>
            </li>
          ) : null}
        </ul>
      </div>
    </aside>
  );
}
